import { read, utils } from 'xlsx'

export const processExcelFile = async (file) => {
    const data = await file.arrayBuffer()
    const workbook = read(data, { raw: true })

    // Filter out sheets we should skip
    const validSheets = workbook.SheetNames.filter((name) => !shouldSkipSheet(name))

    console.log('Processing sheets:', validSheets)
    console.log(
        'Skipping sheets:',
        workbook.SheetNames.filter((name) => shouldSkipSheet(name))
    )

    // Process each sheet
    let studentsData = []
    let staffData = []

    for (const sheetName of validSheets) {
        console.log(`Processing sheet: ${sheetName}`)
        const sheet = workbook.Sheets[sheetName]

        if (!sheet || !sheet['!ref']) {
            console.warn(`Sheet ${sheetName} is empty or invalid`)
            continue
        }

        const { headers, dataStartRow } = findHeadersAndData(sheet)

        if (Object.keys(headers).length === 0 || dataStartRow === null) {
            console.warn(`No valid headers found in sheet: ${sheetName}`)
            continue
        }

        console.log(`Found headers in sheet ${sheetName}:`, headers)
        console.log(`Data starts at row: ${dataStartRow} (Excel row: ${dataStartRow + 1})`)

        const extractedData = extractData(sheet, headers, dataStartRow)

        if (extractedData.length === 0) {
            console.warn(`No data found in sheet: ${sheetName}`)
            continue
        }

        // Determine if student or staff data
        const dataType = guessDataType(headers, sheetName)

        if (dataType === 'student' && studentsData.length === 0) {
            studentsData = extractedData
            console.log(`Identified ${extractedData.length} student records in sheet: ${sheetName}`)
        } else if (dataType === 'staff' && staffData.length === 0) {
            staffData = extractedData
            console.log(`Identified ${extractedData.length} staff records in sheet: ${sheetName}`)
        } else if (studentsData.length === 0 && !dataType) {
            // If we can't determine type and haven't found student data yet, assume it's student data
            studentsData = extractedData
            console.log(`Assuming ${extractedData.length} student records in sheet: ${sheetName}`)
        } else if (staffData.length === 0 && !dataType) {
            // If we can't determine type and haven't found staff data yet, assume it's staff data
            staffData = extractedData
            console.log(`Assuming ${extractedData.length} staff records in sheet: ${sheetName}`)
        }
    }

    return {
        studentsData,
        staffData,
        processedSheets: validSheets,
    }
}

const shouldSkipSheet = (sheetName) => {
    const lowerName = sheetName.toLowerCase()
    return lowerName.includes('checklist') || lowerName.includes('instruction') || lowerName.includes('readme')
}

const findHeadersAndData = (sheet) => {
    const range = utils.decode_range(sheet['!ref'])
    const headerRows = []
    let dataStartRow = null
    let headerRow = null

    // Look for rows that might be headers (non-empty, with text values)
    for (let r = range.s.r; r <= Math.min(range.e.r, range.s.r + 20); r++) {
        let rowValues = []
        let nonEmptyCount = 0

        for (let c = range.s.c; c <= range.e.c; c++) {
            const cellAddress = utils.encode_cell({ r, c })
            const cell = sheet[cellAddress]

            if (cell && cell.v !== undefined && cell.v !== null && cell.v !== '') {
                nonEmptyCount++
                rowValues.push({ col: c, value: cell.v })
            }
        }

        // If row has several non-empty cells, it might be a header row
        if (nonEmptyCount >= 3) {
            headerRows.push({ row: r, values: rowValues })

            // Check if any values look like column headers
            const possibleHeaders = ['name', 'id', 'first', 'last', 'email', 'gender', 'class', 'year', 'username']
            const headerMatches = rowValues.filter((v) => possibleHeaders.some((header) => String(v.value).toLowerCase().includes(header)))

            if (headerMatches.length >= 2) {
                headerRow = r
                dataStartRow = r + 1 // Data starts after header
                break
            }
        }
    }

    // If we found a header row, extract the headers
    if (headerRow !== null) {
        const headers = {}
        const headerCells = headerRows[headerRows.length - 1].values

        headerCells.forEach((cell) => {
            headers[cell.col] = String(cell.value).trim()
        })

        return { headers, dataStartRow, headerRow }
    }

    // Fallback: try to use the first non-empty row as header
    if (headerRows.length > 0) {
        const headers = {}
        const headerCells = headerRows[0].values

        headerCells.forEach((cell) => {
            headers[cell.col] = String(cell.value).trim()
        })

        return { headers, dataStartRow: headerRows[0].row + 1, headerRow: headerRows[0].row }
    }

    return { headers: {}, dataStartRow: null, headerRow: null }
}

const extractData = (sheet, headers, dataStartRow) => {
    if (!sheet || !headers || !dataStartRow) {
        return []
    }

    const range = utils.decode_range(sheet['!ref'])
    const data = []

    // Process each row from the data start row
    for (let r = dataStartRow; r <= range.e.r; r++) {
        const rowData = {}
        let hasValues = false
        let nonEmptyCount = 0

        // For each header, get the corresponding cell value
        Object.entries(headers).forEach(([col, header]) => {
            const cellAddress = utils.encode_cell({ r, c: parseInt(col) })
            const cell = sheet[cellAddress]

            if (cell && cell.v !== undefined && cell.v !== null && cell.v !== '') {
                rowData[header] = cell.v
                hasValues = true
                nonEmptyCount++
            } else {
                rowData[header] = ''
            }
        })

        // Only add rows that have actual values (at least 2 non-empty cells)
        if (hasValues && nonEmptyCount >= 2) {
            data.push(rowData)
        }
    }

    return data
}

const guessDataType = (headers, sheetName) => {
    const headerText = Object.values(headers).join(' ').toLowerCase()
    const sheetNameLower = sheetName.toLowerCase()

    // Check sheet name first
    if (sheetNameLower.includes('student') || sheetNameLower.includes('pupil')) {
        return 'student'
    }

    if (sheetNameLower.includes('staff') || sheetNameLower.includes('teacher')) {
        return 'staff'
    }

    // Then check headers
    if (headerText.includes('student') || headerText.includes('pupil') || headerText.includes('class') || headerText.includes('year') || headerText.includes('grade')) {
        return 'student'
    }

    if (headerText.includes('staff') || headerText.includes('teacher') || headerText.includes('faculty') || headerText.includes('subject')) {
        return 'staff'
    }

    return null
}
